import type { MmObjectType } from "../api/mentalModels";
import type { NowMode } from "../api/now";
import {
  isDistress,
  isFallback,
  type BodySuggestion,
  type CaptureDue,
  type CaptureItem,
  type CaptureKind,
  type CaptureLocale,
  type CaptureSource,
  type CommitRequest,
  type CommitResponse,
  type UnderstandResponse,
  type UnderstandSource,
} from "../api/capture";

// Pure state for the capture sheet (spec 2026-11-02 §3). The whole text is
// always the primary item; parts are suggestions and start unticked.

export type SheetPhase = "understanding" | "review" | "fallback" | "distress" | "saving" | "saved";

export interface DraftPart extends CaptureItem {
  ticked: boolean;
}

export interface SheetState {
  phase: SheetPhase;
  /** Where a failed save returns to (review, fallback or distress). */
  returnTo: SheetPhase | null;
  text: string;
  whole: CaptureItem;
  parts: DraftPart[];
  reflective: boolean;
  /** True when the AI produced the reading (false = kind buttons only). */
  ai: boolean;
  body: BodySuggestion | null;
  /** The existing project the AI matched a "new project" note to, while the note is filed there. */
  projectMatch: string | null;
  /** The mode the AI suggests for working on the note now (changeable); null when none. */
  mode: NowMode | null;
  modeWhy?: string;
  /** The mode the AI suggested; modeWhy explains this one only. */
  suggestedMode?: NowMode | null;
  result: CommitResponse | null;
  error: boolean;
}

export type SheetTarget = "whole" | number;

/** Project option for a part: the project this capture is creating. */
export const NEW_PROJECT = "__new__";

export type SheetAction =
  | { type: "understood"; response: UnderstandResponse }
  | { type: "fail" }
  | { type: "setKind"; target: SheetTarget; kind: CaptureKind }
  | { type: "setMmType"; target: SheetTarget; mmType: MmObjectType }
  | { type: "setDue"; due: CaptureDue | null }
  | { type: "setSpace"; target?: SheetTarget; spaceId: string | null }
  | { type: "setProject"; target?: SheetTarget; projectId: string | null }
  | { type: "newProjectInstead" }
  | { type: "togglePart"; index: number }
  | { type: "setMode"; mode: NowMode }
  | { type: "saving" }
  | { type: "saved"; result: CommitResponse }
  | { type: "saveFailed" };

export function initSheet(text: string): SheetState {
  const trimmed = text.trim();
  return {
    phase: "understanding",
    returnTo: null,
    text: trimmed,
    whole: { text: trimmed, kind: "insight" },
    parts: [],
    reflective: false,
    ai: false,
    body: null,
    projectMatch: null,
    mode: null,
    result: null,
    error: false,
  };
}

/** Kinds that can sit inside a project (a mental model has nowhere to keep one). */
const canJoinProject = (kind: CaptureKind) => kind !== "project" && kind !== "mental_model";

function withKind<T extends CaptureItem>(item: T, kind: CaptureKind): T {
  const next: T = { ...item, kind };
  // Keep an earlier type guess; a mental model always needs a type.
  if (kind === "mental_model" && !next.mmType) next.mmType = "other";
  if (!canJoinProject(kind)) {
    delete next.projectId;
    delete next.inNewProject;
  }
  return next;
}

/** A part starts filed where the whole note is: same space, same project (or the new one). */
function fileLikeWhole(part: DraftPart, whole: CaptureItem): DraftPart {
  const next: DraftPart = { ...part };
  if (whole.spaceId) next.spaceId = whole.spaceId;
  if (canJoinProject(part.kind)) {
    if (whole.kind === "project") next.inNewProject = true;
    else if (whole.projectId) next.projectId = whole.projectId;
  }
  return next;
}

function setProjectOn(item: CaptureItem, projectId: string | null): CaptureItem {
  const next = { ...item };
  delete next.projectId;
  delete next.inNewProject;
  if (projectId === NEW_PROJECT) next.inNewProject = true;
  else if (projectId) next.projectId = projectId;
  return next;
}

function updateTarget(state: SheetState, target: SheetTarget, fn: (item: CaptureItem) => CaptureItem): SheetState {
  if (target === "whole") return { ...state, whole: fn(state.whole) };
  return { ...state, parts: state.parts.map((p, i) => (i === target ? (fn(p) as DraftPart) : p)) };
}

export function sheetReducer(state: SheetState, action: SheetAction): SheetState {
  switch (action.type) {
    case "understood": {
      const r = action.response;
      if (isFallback(r)) return { ...state, phase: "fallback", ai: false, mode: null, modeWhy: undefined, suggestedMode: null };
      if (isDistress(r)) return { ...state, phase: "distress", ai: true, mode: null, modeWhy: undefined, suggestedMode: null };
      const { sameProjectId, ...read } = r.whole;
      // Rather than a second copy of a project they already have, file the note into it.
      const whole: CaptureItem =
        read.kind === "project" && sameProjectId
          ? { ...read, text: state.text, kind: "insight", projectId: sameProjectId }
          : { ...read, text: state.text };
      return {
        ...state,
        phase: "review",
        ai: true,
        whole,
        parts: r.parts.map((p) => fileLikeWhole({ ...p, ticked: false }, whole)),
        reflective: r.reflective,
        mode: r.mode ?? null,
        modeWhy: r.modeWhy,
        suggestedMode: r.mode ?? null,
        body: r.body ?? null,
        projectMatch: whole.projectId === sameProjectId && sameProjectId ? sameProjectId : null,
      };
    }
    case "fail":
      return { ...state, phase: "fallback", ai: false };
    case "setKind": {
      const next = updateTarget(state, action.target, (item) => withKind(item, action.kind));
      if (action.target !== "whole" || action.kind === "project") return next;
      // No new project any more: nothing for the parts to join.
      return { ...next, parts: next.parts.map((p) => setProjectOnIfNew(p)) };
    }
    case "setMmType":
      return updateTarget(state, action.target, (item) => ({ ...item, mmType: action.mmType }));
    case "setDue": {
      const whole = { ...state.whole };
      if (action.due) whole.due = action.due;
      else delete whole.due;
      return { ...state, whole };
    }
    case "setSpace": {
      const target = action.target ?? "whole";
      const setSpace = <T extends CaptureItem>(item: T): T => {
        const next = { ...item };
        if (action.spaceId) next.spaceId = action.spaceId;
        else delete next.spaceId;
        return next;
      };
      const next = updateTarget(state, target, setSpace);
      if (target !== "whole") return next;
      // Parts still filed like the whole note follow it; ones set on their own stay.
      const was = state.whole.spaceId;
      return { ...next, parts: next.parts.map((p) => (p.spaceId === was ? setSpace(p) : p)) };
    }
    case "setProject": {
      const target = action.target ?? "whole";
      const next = updateTarget(state, target, (item) => setProjectOn(item, action.projectId));
      if (target !== "whole") return next;
      const was = state.whole.projectId;
      const parts = next.parts.map((p) =>
        canJoinProject(p.kind) && !p.inNewProject && p.projectId === was
          ? (setProjectOn(p, action.projectId) as DraftPart)
          : p
      );
      return { ...next, parts, projectMatch: null };
    }
    case "newProjectInstead": {
      const match = state.projectMatch;
      const whole = withKind(setProjectOn(state.whole, null), "project");
      const parts = state.parts.map((p) =>
        match && p.projectId === match ? (setProjectOn(p, NEW_PROJECT) as DraftPart) : p
      );
      return { ...state, whole, parts, projectMatch: null };
    }
    case "setMode":
      return { ...state, mode: action.mode };
    case "togglePart":
      return { ...state, parts: state.parts.map((p, i) => (i === action.index ? { ...p, ticked: !p.ticked } : p)) };
    case "saving":
      return { ...state, phase: "saving", returnTo: state.phase === "saving" ? state.returnTo : state.phase, error: false };
    case "saved":
      return { ...state, phase: "saved", result: action.result, returnTo: null, error: false };
    case "saveFailed":
      return { ...state, phase: state.returnTo ?? "review", returnTo: null, error: true };
    default:
      return state;
  }
}

function setProjectOnIfNew(part: DraftPart): DraftPart {
  return part.inNewProject ? (setProjectOn(part, null) as DraftPart) : part;
}

/** Only what each kind uses goes to the server. */
function cleanItem(item: CaptureItem): CaptureItem {
  const out: CaptureItem = { text: item.text, kind: item.kind };
  if (item.kind === "mental_model") out.mmType = item.mmType ?? "other";
  if (item.kind === "action" && item.due) out.due = item.due;
  if (item.spaceId) out.spaceId = item.spaceId;
  if (canJoinProject(item.kind)) {
    if (item.projectId) out.projectId = item.projectId;
    else if (item.inNewProject) out.inNewProject = true;
  }
  return out;
}

export function toCommitBody(
  state: SheetState,
  opts: { source: CaptureSource; locale: CaptureLocale; overrideKind?: CaptureKind; fromDistress?: boolean }
): CommitRequest {
  const whole = opts.overrideKind ? withKind(state.whole, opts.overrideKind) : state.whole;
  const { inNewProject: _ignored, ...wholeItem } = cleanItem(whole);
  const joinable = whole.kind === "project";
  return {
    text: state.text,
    source: opts.source,
    locale: opts.locale,
    whole: wholeItem,
    ...(opts.fromDistress ? { fromDistress: true } : {}),
    parts: state.parts
      .filter((p) => p.ticked)
      .map(cleanItem)
      .map((p) => {
        if (joinable || !p.inNewProject) return p;
        const { inNewProject: _drop, ...rest } = p;
        return rest;
      }),
  };
}

/** PWA share-target params (manifest share_target: title, text, url) joined as one text. */
export function sharedTextFrom(params: URLSearchParams): string {
  return [params.get("title"), params.get("text"), params.get("url")]
    .map((s) => s?.trim())
    .filter(Boolean)
    .join("\n");
}

/** /capture is reached by a share, the "Quick capture" shortcut (?src=shortcut) or directly. */
export function captureSourceFrom(params: URLSearchParams): UnderstandSource {
  if (sharedTextFrom(params)) return "share";
  return params.get("src") === "shortcut" ? "shortcut" : "capture_page";
}
